/* Copyright (c) 2017 Backpack Health LLC */

/* global window */
import React from 'react';
import * as _ from 'lodash';
import { injectIntl } from 'react-intl';

import { ContactModelType } from '../../../../models/ContactModel';
import { ShareCardColorModel } from '../../../appAuthorized/dataTransformComponents/ColorShareCard';
import { ShareCard } from '../../../../models/ShareCardModel';

import { ShareCardCategory } from './ShareCardCategory';
import { ShareCardCategorySection } from './ShareCardCategorySection';
import { ShareCardItemRow } from './ShareCardItemRow';
import { Icons } from '../../../../bphComponents/bundle';
import { ContactSummaryText } from '../../../components/ContactSummaryText';
import { useShareCardForActiveProfile } from '../../../hooks/useShareCard';

const ShareCardEditContactListItem = (props) => {
  const { contact } = props;
  return (
    <div>
      <div className="font-body font-color-gsDarkest">{contact.name}</div>
      <div className="font-footnote font-color-gsDark">
        <ContactSummaryText data={contact} isSingleLine={true} />
      </div>
    </div>
  );
};

interface Props {
  expandedCategory: string;
  isProvider: boolean;
  shareCardColorModel: ShareCardColorModel;
  contacts: ContactModelType[];
  shareCardData: ShareCard;
  toggleExpandCategory(e, v): void;
  intl: any;
  onUpdateShareCard(data: ShareCard): void;
}

const ShareCardEditContacts: React.FC<Props> = (props) => {
  const { isProvider, onUpdateShareCard, shareCardData, expandedCategory, shareCardColorModel, contacts, intl, toggleExpandCategory } = props;

  const [, scActions] = useShareCardForActiveProfile();
  const shareCard = scActions.instantiate(shareCardData);

  const shareCardContactGroup = shareCard.createContactGroup(contacts);
  const titlePhrase = isProvider
    ? { id: 'shareCard.providers.heading', values: {} }
    : { id: 'shareCard.contacts.heading', values: {} };
  const categoryName = isProvider ? 'provider-contacts' : 'contacts';

  const pastCurrentContacts = isProvider
    ? {
      current: [],
      past: [],
      ..._.groupBy(contacts, (p) => (p.current ? 'current' : 'past')),
    } : {
      current: contacts,
      past: [],
    };

  return (
    <ShareCardCategory
      showTotals={true}
      icon={() => <Icons.IconProvidersContacts.Medium className="fill-color-gsMedDark" />}
      group={shareCardContactGroup}
      shareCardColorModel={shareCardColorModel}
      titlePhraseData={titlePhrase}
      isExpanded={expandedCategory === categoryName}
      toggleExpand={(e) => { toggleExpandCategory(e.target, categoryName); }}
      toggleAll={() => {
        shareCardContactGroup.toggleAll();
        onUpdateShareCard(shareCard.data);
      }}
    >
      {
        pastCurrentContacts.current.map((entry, entryIdx) => {
          const pShareCardItem = shareCard.createContactItem(entry.id);
          return (
            <ShareCardItemRow
              key={entryIdx}
              cssColor={shareCardColorModel.cssColor()}
              isExcluded={pShareCardItem.isExcluded()}
              isRequired={pShareCardItem.isRequired()}
              isSelected={pShareCardItem.isIncluded()}
              onToggle={() => {
                pShareCardItem.toggle();
                onUpdateShareCard(shareCard.data);
              }}
            >
              <ShareCardEditContactListItem contact={entry} />
            </ShareCardItemRow>
          );
        })
      }

      <ShareCardCategorySection
        titleShowHidden={{ id: 'bpAll.hidePast.button.label', values: { string1: intl.formatMessage({ id: 'list.provider.name' }, { digit1: pastCurrentContacts.past.length }) } }}
        titleHideRevealed={{ id: 'bpAll.showPast.button.label', values: { string1: intl.formatMessage({ id: 'list.provider.name' }, { digit1: pastCurrentContacts.past.length }) } }}
        shareCardColorModel={shareCardColorModel}
      >
        {
          pastCurrentContacts.past.map((entry, entryIdx) => {
            const pShareCardItem = shareCard.createContactItem(entry.id);
            return (
              <ShareCardItemRow
                key={entryIdx}
                cssColor={shareCardColorModel.cssColor()}
                isExcluded={pShareCardItem.isExcluded()}
                isRequired={pShareCardItem.isRequired()}
                isSelected={pShareCardItem.isIncluded()}
                onToggle={() => {
                  pShareCardItem.toggle();
                  onUpdateShareCard(shareCard.data);
                }}
              >
                <ShareCardEditContactListItem contact={entry} />
              </ShareCardItemRow>
            );
          })
        }
      </ShareCardCategorySection>
    </ShareCardCategory>
  );
};

export default injectIntl(ShareCardEditContacts);
